import React, { useState } from 'react';
import { BRAND_NAME, BRAND_WORDMARK } from '../brand';

const BrandingExplorer = () => {
    const [activeTab, setActiveTab] = useState('logos');
    const [darkMode, setDarkMode] = useState(false);

    const colors = {
        light: {
            bg: '#FAFAFA',
            card: '#FFFFFF',
            text: '#1A1A2E',
            textMuted: '#6B7280',
            border: '#E5E7EB'
        },
        dark: {
            bg: '#0F0F1A',
            card: '#1A1A2E',
            text: '#F9FAFB',
            textMuted: '#9CA3AF',
            border: '#2D2D44'
        }
    };

    const theme = darkMode ? colors.dark : colors.light;

    // Actual Application Domain Colors (from design/defaults)
    const accentColors = [
        { name: 'Job Hunt', hex: '#4ECDC4', desc: 'Teal - Calm focus' },
        { name: 'Finance', hex: '#87A878', desc: 'Sage - Growth & Stability' },
        { name: 'Business', hex: '#6366F1', desc: 'Indigo - Modern Professional' },
        { name: 'Parenthood', hex: '#FF6B6B', desc: 'Coral - Warmth & Vitality' },
        { name: 'Health', hex: '#38BDF8', desc: 'Sky Blue - Clarity' },
        { name: 'Education', hex: '#A78BFA', desc: 'Purple - Wisdom' }
    ];

    // Logo Option A: Overlapping Circles
    const LogoOverlappingCircles = ({ color = '#4ECDC4', size = 60 }: { color?: string; size?: number }) => (
        <svg width={size} height={size} viewBox="0 0 60 60">
            <circle cx="20" cy="30" r="15" fill={color} opacity="0.7" />
            <circle cx="30" cy="18" r="15" fill={color} opacity="0.5" />
            <circle cx="40" cy="30" r="15" fill={color} opacity="0.6" />
        </svg>
    );

    // Logo Option B: Segmented Circle
    const LogoSegmentedCircle = ({ color = '#4ECDC4', size = 60 }: { color?: string; size?: number }) => (
        <svg width={size} height={size} viewBox="0 0 60 60">
            <path d="M30 5 A25 25 0 0 1 55 30 L30 30 Z" fill={color} opacity="0.9" />
            <path d="M55 30 A25 25 0 0 1 30 55 L30 30 Z" fill={color} opacity="0.7" />
            <path d="M30 55 A25 25 0 0 1 5 30 L30 30 Z" fill={color} opacity="0.5" />
            <path d="M5 30 A25 25 0 0 1 30 5 L30 30 Z" fill={color} opacity="0.3" />
        </svg>
    );

    // Logo Option C: Abstract D Gateway
    const LogoDGateway = ({ color = '#4ECDC4', size = 60 }: { color?: string; size?: number }) => (
        <svg width={size} height={size} viewBox="0 0 60 60">
            <path
                d="M15 10 L15 50 L35 50 Q50 50 50 30 Q50 10 35 10 Z"
                fill="none"
                stroke={color}
                strokeWidth="4"
                strokeLinecap="round"
            />
            <path
                d="M25 20 L40 30 L25 40"
                fill="none"
                stroke={color}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.7"
            />
        </svg>
    );

    // Logo Option D: Unified Hexagons
    const LogoHexagons = ({ color = '#4ECDC4', size = 60 }: { color?: string; size?: number }) => (
        <svg width={size} height={size} viewBox="0 0 60 60">
            <polygon points="30,5 45,15 45,35 30,45 15,35 15,15" fill={color} opacity="0.3" />
            <polygon points="30,12 40,18 40,32 30,38 20,32 20,18" fill={color} opacity="0.6" />
            <polygon points="30,19 35,22 35,28 30,31 25,28 25,22" fill={color} opacity="1" />
        </svg>
    );

    // Logo Option E: Flowing Paths
    const LogoFlowingPaths = ({ color = '#4ECDC4', size = 60 }: { color?: string; size?: number }) => (
        <svg width={size} height={size} viewBox="0 0 60 60">
            <path
                d="M10 30 Q20 10 30 30 Q40 50 50 30"
                fill="none"
                stroke={color}
                strokeWidth="3"
                strokeLinecap="round"
                opacity="0.5"
            />
            <path
                d="M10 25 Q20 15 30 25 Q40 35 50 25"
                fill="none"
                stroke={color}
                strokeWidth="3"
                strokeLinecap="round"
                opacity="0.7"
            />
            <path
                d="M10 35 Q20 25 30 35 Q40 45 50 35"
                fill="none"
                stroke={color}
                strokeWidth="3"
                strokeLinecap="round"
                opacity="0.9"
            />
        </svg>
    );

    // Logo Option F: Corner Bracket D
    const LogoBracketD = ({ color = '#4ECDC4', size = 60 }: { color?: string; size?: number }) => (
        <svg width={size} height={size} viewBox="0 0 60 60">
            <path
                d="M18 12 L18 48 M18 12 L38 12 Q48 12 48 22 L48 38 Q48 48 38 48 L18 48"
                fill="none"
                stroke={color}
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const logoOptions = [
        {
            name: 'Overlapping Domains',
            component: LogoOverlappingCircles,
            desc: 'Three life domains converging into harmony'
        },
        {
            name: 'Segmented Wheel',
            component: LogoSegmentedCircle,
            desc: 'Life areas as unified sections'
        },
        {
            name: 'D Gateway',
            component: LogoDGateway,
            desc: 'Enter your organized space'
        },
        {
            name: 'Nested Hexagons',
            component: LogoHexagons,
            desc: 'Layers of organization, centered calm'
        },
        {
            name: 'Flowing Balance',
            component: LogoFlowingPaths,
            desc: 'Life domains in harmony'
        },
        {
            name: 'Bold D',
            component: LogoBracketD,
            desc: 'Strong, confident, minimal'
        }
    ];

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.bg,
            color: theme.text,
            padding: '24px',
            fontFamily: 'Inter, system-ui, sans-serif'
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '32px'
            }}>
                <div>
                    <h1 style={{ fontSize: '32px', fontWeight: 600, margin: 0 }}>
                        {BRAND_NAME} Branding
                    </h1>
                    <p style={{ color: theme.textMuted, marginTop: '8px' }}>
                        Visual identity exploration
                    </p>
                </div>
                <button
                    onClick={() => setDarkMode(!darkMode)}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: `1px solid ${theme.border}`,
                        backgroundColor: theme.card,
                        color: theme.text,
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    {darkMode ? '☀️ Light' : '🌙 Dark'}
                </button>
            </div>

            {/* Tabs */}
            <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '24px',
                borderBottom: `1px solid ${theme.border}`,
                paddingBottom: '16px'
            }}>
                {['logos', 'colors', 'combinations', 'typography'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: activeTab === tab ? '#4ECDC4' : theme.card,
                            color: activeTab === tab ? '#fff' : theme.text,
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 500,
                            textTransform: 'capitalize'
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Logo Options */}
            {activeTab === 'logos' && (
                <div>
                    <h2 style={{ fontSize: '20px', fontWeight: 500, marginBottom: '20px' }}>
                        Logo Mark Options
                    </h2>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '20px'
                    }}>
                        {logoOptions.map((logo, idx) => (
                            <div
                                key={idx}
                                style={{
                                    backgroundColor: theme.card,
                                    borderRadius: '16px',
                                    padding: '24px',
                                    border: `1px solid ${theme.border}`
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    marginBottom: '16px'
                                }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: darkMode ? '#2D2D44' : '#F3F4F6',
                                        borderRadius: '12px'
                                    }}>
                                        <logo.component color="#4ECDC4" size={50} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                                            {logo.name}
                                        </h3>
                                        <p style={{ margin: '4px 0 0', color: theme.textMuted, fontSize: '13px' }}>
                                            {logo.desc}
                                        </p>
                                    </div>
                                </div>

                                {/* Color variations */}
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {accentColors.slice(0, 4).map(color => (
                                        <div
                                            key={color.hex}
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: darkMode ? '#1A1A2E' : '#fff',
                                                borderRadius: '8px',
                                                border: `1px solid ${theme.border}`
                                            }}
                                        >
                                            <logo.component color={color.hex} size={28} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Color Palette */}
            {activeTab === 'colors' && (
                <div>
                    <h2 style={{ fontSize: '20px', fontWeight: 500, marginBottom: '20px' }}>
                        Accent Color Options
                    </h2>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                        marginBottom: '32px'
                    }}>
                        {accentColors.map((color, idx) => (
                            <div
                                key={idx}
                                style={{
                                    backgroundColor: theme.card,
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    border: `1px solid ${theme.border}`
                                }}
                            >
                                <div style={{
                                    height: '80px',
                                    backgroundColor: color.hex,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <span style={{ color: '#fff', fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                                        {color.hex}
                                    </span>
                                </div>
                                <div style={{ padding: '12px' }}>
                                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>
                                        {color.name}
                                    </h3>
                                    <p style={{ margin: '4px 0 0', color: theme.textMuted, fontSize: '12px' }}>
                                        {color.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <h2 style={{ fontSize: '20px', fontWeight: 500, marginBottom: '20px' }}>
                        Full Palette (Light Mode)
                    </h2>
                    <div style={{
                        backgroundColor: theme.card,
                        borderRadius: '12px',
                        padding: '24px',
                        border: `1px solid ${theme.border}`,
                        display: 'flex',
                        gap: '4px',
                        marginBottom: '24px'
                    }}>
                        {['#FAFAFA', '#F3F4F6', '#E5E7EB', '#9CA3AF', '#6B7280', '#374151', '#1A1A2E'].map((c, i) => (
                            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{ height: '60px', backgroundColor: c, borderRadius: '8px' }} />
                                <span style={{ fontSize: '10px', color: theme.textMuted }}>{c}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Combinations */}
            {activeTab === 'combinations' && (
                <div>
                    <h2 style={{ fontSize: '20px', fontWeight: 500, marginBottom: '20px' }}>
                        Logo + Wordmark Combinations
                    </h2>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '20px'
                    }}>
                        {/* Horizontal lockups */}
                        {[
                            { Logo: LogoOverlappingCircles, color: '#4ECDC4', name: 'Job Hunt (Teal)' },
                            { Logo: LogoSegmentedCircle, color: '#6366F1', name: 'Business (Indigo)' },
                            { Logo: LogoHexagons, color: '#87A878', name: 'Finance (Sage)' },
                            { Logo: LogoBracketD, color: '#FF6B6B', name: 'Parenthood (Coral)' }
                        ].map((combo, idx) => (
                            <div
                                key={idx}
                                style={{
                                    backgroundColor: theme.card,
                                    borderRadius: '16px',
                                    padding: '32px',
                                    border: `1px solid ${theme.border}`
                                }}
                            >
                                <p style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '16px' }}>
                                    {combo.name}
                                </p>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginBottom: '24px'
                                }}>
                                    <combo.Logo color={combo.color} size={44} />
                                    <span style={{
                                        fontSize: '26px',
                                        fontWeight: 500,
                                        letterSpacing: '0.01em',
                                        color: theme.text
                                    }}>
                                        {BRAND_WORDMARK}
                                    </span>
                                </div>

                                {/* Small version */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    opacity: 0.7
                                }}>
                                    <combo.Logo color={combo.color} size={24} />
                                    <span style={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: theme.text
                                    }}>
                                        {BRAND_WORDMARK}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* App Icon Preview */}
                    <h2 style={{ fontSize: '20px', fontWeight: 500, margin: '32px 0 20px' }}>
                        App Icon Preview
                    </h2>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {[
                            { Logo: LogoOverlappingCircles, bg: '#4ECDC4' },
                            { Logo: LogoSegmentedCircle, bg: '#6366F1' },
                            { Logo: LogoHexagons, bg: '#87A878' },
                            { Logo: LogoBracketD, bg: '#1A1A2E' }
                        ].map((icon, idx) => (
                            <div
                                key={idx}
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '18px',
                                    backgroundColor: icon.bg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }}
                            >
                                <icon.Logo color="#FFFFFF" size={48} />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Typography */}
            {activeTab === 'typography' && (
                <div>
                    <h2 style={{ fontSize: '20px', fontWeight: 500, marginBottom: '20px' }}>
                        Typography Scale
                    </h2>
                    <div style={{
                        backgroundColor: theme.card,
                        borderRadius: '16px',
                        padding: '32px',
                        border: `1px solid ${theme.border}`,
                        marginBottom: '24px'
                    }}>
                        <div style={{ marginBottom: '24px' }}>
                            <span style={{ fontSize: '12px', color: theme.textMuted }}>Wordmark</span>
                            <div style={{
                                fontSize: '48px',
                                fontWeight: 500,
                                letterSpacing: '0.01em',
                                marginTop: '8px'
                            }}>
                                {BRAND_WORDMARK}
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <span style={{ fontSize: '12px', color: theme.textMuted }}>H1 - Page Title</span>
                            <div style={{ fontSize: '32px', fontWeight: 600, marginTop: '8px' }}>
                                Your life, organized
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <span style={{ fontSize: '12px', color: theme.textMuted }}>H2 - Section</span>
                            <div style={{ fontSize: '24px', fontWeight: 500, marginTop: '8px' }}>
                                Job Hunt • Finance • Parenthood
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <span style={{ fontSize: '12px', color: theme.textMuted }}>Body</span>
                            <div style={{ fontSize: '16px', lineHeight: 1.6, marginTop: '8px', maxWidth: '500px' }}>
                                {BRAND_NAME} helps busy professionals manage the chaos of modern life.
                                From job applications to bills to school permissions—everything in one calm space.
                            </div>
                        </div>

                        <div>
                            <span style={{ fontSize: '12px', color: theme.textMuted }}>Caption</span>
                            <div style={{ fontSize: '13px', color: theme.textMuted, marginTop: '8px' }}>
                                Last synced 2 minutes ago • 12 items need attention
                            </div>
                        </div>
                    </div>

                    <h2 style={{ fontSize: '20px', fontWeight: 500, marginBottom: '20px' }}>
                        Tagline Options
                    </h2>
                    <div style={{
                        backgroundColor: theme.card,
                        borderRadius: '16px',
                        padding: '32px',
                        border: `1px solid ${theme.border}`
                    }}>
                        {[
                            'Your life, organized.',
                            'Breathe. You\'ve got this.',
                            'Every domain, one view.',
                            'Calm in the chaos.',
                            'Life management, simplified.',
                            'All your domains. One place.'
                        ].map((tagline, idx) => (
                            <div
                                key={idx}
                                style={{
                                    padding: '16px 0',
                                    borderBottom: idx < 5 ? `1px solid ${theme.border}` : 'none',
                                    fontSize: '20px',
                                    fontWeight: 400,
                                    color: idx < 2 ? theme.text : theme.textMuted
                                }}
                            >
                                {tagline}
                                {idx === 0 && (
                                    <span style={{
                                        marginLeft: '12px',
                                        fontSize: '12px',
                                        backgroundColor: '#4ECDC4',
                                        color: '#fff',
                                        padding: '4px 8px',
                                        borderRadius: '4px'
                                    }}>
                                        Recommended
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BrandingExplorer;
